import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { ContactData } from "components/common/ContactSearchAutoComplete";
import { useTranslation } from "react-i18next";
import { GET_INDIVIDUALS_FOR_BUSINESS } from "graphql/contact";
import ContactStatus from "enums/ContactStatus";
import { fullName } from "utils/fullName";

interface IndividualSearchForBusinessProps {
  businessId: string;
  hideLabel?: boolean;
  required?: boolean;
  errorText?: string;
  helperText?: string;
  customPlaceHolder?: string;
  refetchIndex?: number;
  onSelectIndividual: (individual: ContactData | null) => void;
}

/**
 * The individuals-under-a-business picker. Local filtering, so the DS marks its
 * own matches.
 *
 * "Create contact to attach to business" was a fake contact at the head of the
 * list; it is a pinned `topAction` now. Callers still receive the same
 * `create-contact` sentinel so their branch is unchanged, but the id can no
 * longer arrive as an ordinary selection.
 */
const IndividualSearchForBusiness: React.FC<
  IndividualSearchForBusinessProps
> = ({
  businessId,
  hideLabel = false,
  required = false,
  errorText = "",
  helperText = "",
  customPlaceHolder = "Select Individual to Invite",
  refetchIndex = 0,
  onSelectIndividual,
}) => {
  const { t } = useTranslation();
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(
    null,
  );

  const { data, loading } = useQuery(GET_INDIVIDUALS_FOR_BUSINESS, {
    variables: { contactId: businessId, status: ContactStatus.Active },
    // `refetchIndex` is the caller's way of saying the list is stale.
    fetchPolicy: refetchIndex > 0 ? "cache-and-network" : "cache-first",
  });

  const contacts: ContactData[] = useMemo(
    () => data?.individualsForBusiness ?? [],
    [data],
  );

  const options: SelectOption[] = useMemo(
    () =>
      contacts.map((contact) => {
        const name = fullName(contact);
        return {
          id: contact.id,
          label: name ? `${name} (${contact.email})` : contact.email,
        };
      }),
    [contacts],
  );

  return (
    <Select
      label={hideLabel ? undefined : t(customPlaceHolder)}
      aria-label={hideLabel ? t(customPlaceHolder) : undefined}
      placeholder={hideLabel ? t(customPlaceHolder) : undefined}
      searchable
      required={required}
      loading={loading}
      errorText={errorText || undefined}
      helperText={helperText || undefined}
      options={options}
      topAction={{
        label: t("Create contact to attach to business"),
        onClick: () =>
          onSelectIndividual({
            id: "create-contact",
            firstName: "",
            lastName: "",
            email: "",
          } as ContactData),
      }}
      value={selectedContact?.id ?? null}
      onChange={(id) => {
        const contact = contacts.find((c) => c.id === id) ?? null;
        setSelectedContact(contact);
        onSelectIndividual(contact);
      }}
    />
  );
};

export default IndividualSearchForBusiness;
